import { Component, OnInit } from "@angular/core";
import { ApprovalStatus } from "../../../core/models/invoice-preview/approvalStatus";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { v4 as uuidv4 } from "uuid";
import { ActivatedRoute } from "@angular/router";
import { GetBillingDetailsForEmailsDto } from "../../../core/models/invoice-preview/getBillingDetailsForEmailsDto";
import * as MicrosoftGraph from "@microsoft/microsoft-graph-types";
import { UpdateInvoicePreviewDispositionStatusDto } from "../../../core/models/invoice-preview/updateInvoicePreviewDispositionStatusDto";
import { SalesOrderNotes } from "../../../core/models/orders/salesOrderNotes";
import { GetUserDto } from "../../../core/models/users/users/getUserDto";
import { InvoicePreviewSummaryService } from "../../../core/services/invoice-preview-summary.service";
import { LoaderService } from "../../../core/services/loader.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { MsalApiService } from "../../../core/services/msal-api.service";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";

@UntilDestroy()
@Component({
  selector: "jni-invoice-preview-disposition",
  templateUrl: "./invoice-preview-disposition.component.html",
  styleUrls: ["./invoice-preview-disposition.component.scss"],
  imports: [SelectModule, FormsModule, ButtonModule],
})
export class InvoicePreviewDispositionComponent implements OnInit {
  invoicePreviewStatus: ApprovalStatus[] = [
    {
      id: "Open",
      name: "Open",
    },
    {
      id: "Approved",
      name: "Approved",
    },
    {
      id: "ApproveSplitPending",
      name: "Approve Split Pending",
    },
    {
      id: "WrongCosts",
      name: "Wrong Costs",
    },
    {
      id: "WrongSell",
      name: "Wrong Sell",
    },
    {
      id: "WrongTax",
      name: "Wrong Tax",
    },
    {
      id: "WrongFreight",
      name: "Wrong Freight",
    },
    {
      id: "WrongOther",
      name: "Wrong Other",
    },
  ];
  quickQuote = "";
  defaultInvoicePreviewStatus = "Open";
  saleOrderId = "";
  invoicePreviewNumber = "";
  customerPONumber = "";

  billingDetailsForEmails: GetBillingDetailsForEmailsDto =
    {} as GetBillingDetailsForEmailsDto;
  userInfo: GetUserDto = {} as GetUserDto;

  constructor(
    private _invoicePreviewSummaryService: InvoicePreviewSummaryService,
    private _loaderService: LoaderService,
    private _toastrService: JniToastrService,
    private _activatedRoute: ActivatedRoute,
    private _msalApisService: MsalApiService
  ) {
    this._activatedRoute.params.subscribe(
      params => (this.invoicePreviewNumber = params["id"])
    );
  }

  ngOnInit(): void {
    this.getInvoicePreviewSummary(this.invoicePreviewNumber);
    this.getMyProfile();
  }

  onApprovalSelection(approvalStatus: SelectChangeEvent) {
    this.defaultInvoicePreviewStatus = approvalStatus.value;
  }

  onQuickQuote() {
    this._loaderService.setLoaderState(true);

    if (!this.quickQuote) {
      return;
    }

    const saleOrderFormObject = {
      description: "",
      id: 0,
      note: this.quickQuote,
      portalKey: "",
      purchaseOrderStatus: false,
      salesOrderId: "",
      type: "Billing note",
    } as SalesOrderNotes;

    if (this.saleOrderId) {
      saleOrderFormObject.salesOrderId = this.saleOrderId;
      saleOrderFormObject.portalKey = uuidv4();
    }

    this._invoicePreviewSummaryService
      .AddSalesOrderNotes(saleOrderFormObject)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this._loaderService.setLoaderState(false);
          this.quickQuote = "";
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastrService.error(
            "Error",
            res.message || "Error adding order note"
          );
        },
      });
  }

  updateInvoicePreviewStatus() {
    this._loaderService.setLoaderState(true);
    const invoicePreviewStatusObject = {
      proposalId: this.invoicePreviewNumber,
      approvalStatus: this.defaultInvoicePreviewStatus,
    } as UpdateInvoicePreviewDispositionStatusDto;

    this._invoicePreviewSummaryService
      .UpdateInvoicePreviewDispositionStatus(invoicePreviewStatusObject)
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.data.Status.toLowerCase() === "success") {
            this._toastrService.success(
              "Success",
              `Approval status update successfully`
            );

            this.getBillingDetailsForEmails(this.invoicePreviewNumber);
          } else {
            this._toastrService.error(
              "Error",
              `Error updating approval status`
            );
          }

          this.getInvoicePreviewSummary(this.invoicePreviewNumber);
        },
        error: () => {
          this._loaderService.setLoaderState(false);
          this._toastrService.error("Error", `Error updating approval status`);
        },
      });
  }

  getInvoicePreviewSummary(invoicePreviewNumber: string) {
    this.quickQuote = "";
    this._loaderService.setLoaderState(true);
    this._invoicePreviewSummaryService
      .getInvoicePreviewSummary(invoicePreviewNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.data) {
            this.saleOrderId = res.data.orderNumber;
            this.defaultInvoicePreviewStatus = res.data.status;
            this.customerPONumber = res.data.clientPO;
          } else {
            this._toastrService.error(
              "Error",
              res.message || `Unable to Fetch Invoice Preview`
            );
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }

  getMyProfile() {
    this._invoicePreviewSummaryService
      .GetMyProfile()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.status.toLowerCase() == "success") {
            this.userInfo = res.data;
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }
  getBillingDetailsForEmails(proposalId: string) {
    this._invoicePreviewSummaryService
      .getBillingDetailsForEmails(proposalId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.status.toLowerCase() == "success") {
            this.billingDetailsForEmails = res.data;
            this.sendApprovalStatusEmail(this.billingDetailsForEmails);
          }
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastrService.error(
            "Error",
            res.message || "Error adding order note"
          );
        },
      });
  }
  sendApprovalStatusEmail(details: GetBillingDetailsForEmailsDto) {
    const toRecipients: MicrosoftGraph.Recipient[] = [
      {
        emailAddress: {
          address: details.billerEmail,
          name: details.billerEmail,
        },
      },
    ];
    const sender: MicrosoftGraph.Recipient = {
      emailAddress: {
        address: this.userInfo.sendEmailAs,
        name: this.userInfo.displayEmailAs,
      },
    };
    let mailMessage: MicrosoftGraph.Message = {};
    mailMessage = {
      subject: details.subject,
      body: {
        contentType: "html",
        content: details.html,
      },
      toRecipients: toRecipients,
      sender: sender,
    };
    const mail = {
      message: mailMessage,
    };
    this._msalApisService.sendMail(mail).subscribe({
      next: () => {
        this._loaderService.setLoaderState(false);
      },
      error: () => {
        this._loaderService.setLoaderState(false);
      },
    });
  }

  onSubmit() {
    this.onQuickQuote();
    this.updateInvoicePreviewStatus();
  }
}
